import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  XMarkIcon,
  ChevronDownIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  TruckIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isMobile = false, onClose }) => {
  const { user } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Master']);

  const menuGroups = [
    // Dashboard - standalone (no group)
    {
      type: 'item',
      path: '/dashboard',
      label: 'Dashboard',
      icon: <HomeIcon className="w-5 h-5" />,
      roles: ['ADMIN', 'MANAGER', 'FIELD_TEAM']
    },
    // Enquiries - standalone
    {
      type: 'item',
      path: '/enquiries',
      label: 'Enquiries',
      icon: <ClipboardDocumentListIcon className="w-5 h-5" />,
      roles: ['ADMIN', 'MANAGER', 'FIELD_TEAM']
    },
    // Quotations - standalone
    {
      type: 'item',
      path: '/quotations',
      label: 'Quotations',
      icon: <DocumentTextIcon className="w-5 h-5" />,
      roles: ['ADMIN', 'MANAGER']
    },
    // Log Sheets - standalone
    {
      type: 'item',
      path: '/log-sheets',
      label: 'Log Sheets',
      icon: <ClipboardDocumentListIcon className="w-5 h-5" />,
      roles: ['ADMIN', 'MANAGER', 'FIELD_TEAM']
    },
    // Invoices - standalone (Admin only)
    {
      type: 'item',
      path: '/invoices',
      label: 'Invoices',
      icon: <DocumentDuplicateIcon className="w-5 h-5" />,
      roles: ['ADMIN']
    },
    // Reports - standalone (Admin only)
    {
      type: 'item',
      path: '/reports',
      label: 'Reports',
      icon: <ChartBarIcon className="w-5 h-5" />,
      roles: ['ADMIN']
    },
    // Service Calendar - standalone (Admin only)
    {
      type: 'item',
      path: '/service-calendar',
      label: 'Service Calendar',
      icon: <CalendarDaysIcon className="w-5 h-5" />,
      roles: ['ADMIN']
    },
    // Master Group
    {
      type: 'group',
      label: 'Master',
      roles: ['ADMIN', 'MANAGER', 'FIELD_TEAM'],
      items: [
        {
          path: '/users',
          label: 'User Management',
          icon: <UsersIcon className="w-5 h-5" />,
          roles: ['ADMIN']
        },
        {
          path: '/bore-sizes',
          label: 'Bore Size',
          icon: <CircleStackIcon className="w-5 h-5" />,
          roles: ['ADMIN']
        },
        {
          path: '/price-slabs',
          label: 'Price Slabs',
          icon: <CurrencyDollarIcon className="w-5 h-5" />,
          roles: ['ADMIN']
        },
        {
          path: '/items',
          label: 'Item Master',
          icon: <CubeIcon className="w-5 h-5" />,
          roles: ['ADMIN']
        },
        {
          path: '/rigs',
          label: 'Rig Master',
          icon: <TruckIcon className="w-5 h-5" />,
          roles: ['ADMIN']
        }
      ]
    }
  ];

  const filteredMenuGroups = menuGroups.map(group => {
    if (group.type === 'item') {
      return group.roles.includes(user?.role || '') ? group : null;
    } else if (group.type === 'group' && group.items) {
      const filteredItems = group.items.filter(item => 
        item.roles.includes(user?.role || '')
      );
      return filteredItems.length > 0 ? { ...group, items: filteredItems } : null;
    }
    return null;
  }).filter((group): group is NonNullable<typeof group> => group !== null);

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => 
      prev.includes(groupLabel) 
        ? prev.filter(label => label !== groupLabel)
        : [...prev, groupLabel]
    );
  };


  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        <h3 className="font-bold text-lg text-white">
          Borewell System
        </h3>
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors duration-200 md:hidden"
          >
            <XMarkIcon className="w-5 h-5 text-gray-300 hover:text-red-300 transition-colors duration-200" />
          </button>
        )}
      </div>
      
      <nav className="py-6 px-3 flex-1 overflow-y-auto">
        <ul className="space-y-2">
          {filteredMenuGroups.map((group) => {
            if (group.type === 'item') {
              return (
                <li 
                  key={group.path}
                >
                  <NavLink
                    to={group.path!}
                    onClick={() => isMobile && onClose?.()}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-3 rounded-lg transition-colors duration-200 group ${
                        isActive 
                          ? 'bg-blue-600 text-white border-l-4 border-blue-400' 
                          : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`
                    }
                  >
                    <span className="min-w-[24px] flex items-center justify-center mr-3">
                      {group.icon}
                    </span>
                    <span className="font-medium">
                      {group.label}
                    </span>
                  </NavLink>
                </li>
              );
            } else if (group.type === 'group') {
              const isExpanded = expandedGroups.includes(group.label);
              
              return (
                <li
                  key={group.label}
                  className="space-y-1"
                >
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors duration-200 group ${
                      isExpanded 
                        ? 'bg-blue-600 text-white shadow-lg' 
                        : 'text-slate-300 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-3 transition-colors duration-200 ${
                        isExpanded 
                          ? 'bg-white' 
                          : 'bg-current opacity-60 group-hover:opacity-80'
                      }`}></span>
                      {group.label}
                    </span>
                    <span
                      className={`transition-transform duration-200 ${
                        isExpanded 
                          ? 'text-white rotate-0' 
                          : 'text-slate-400 group-hover:text-slate-200 -rotate-90'
                      }`}
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                    </span>
                  </button>
                  
                  <div
                    className={`overflow-visible transition-all duration-300 ease-in-out ${
                      isExpanded 
                        ? 'max-h-96 opacity-100' 
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul
                      className="space-y-1 ml-4 border-l-2 border-blue-600 pl-3 pt-2"
                    >
                      {group.items?.map((item) => (
                          <li
                            key={item.path}
                          >
                            <NavLink
                              to={item.path}
                              onClick={() => isMobile && onClose?.()}
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2.5 rounded-lg transition-colors duration-200 group ${
                                  isActive 
                                    ? 'bg-blue-600 text-white border-l-4 border-blue-400' 
                                    : 'text-slate-300 hover:text-white hover:bg-slate-800'
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  <span className="min-w-[20px] flex items-center justify-center mr-3">
                                    {item.icon}
                                  </span>
                                  <span className="font-medium text-sm">
                                    {item.label}
                                  </span>
                                  <span className={`ml-auto transition-all duration-200 ${
                                    isActive 
                                      ? 'w-2 h-2 bg-white rounded-full scale-100' 
                                      : 'w-1 h-1 bg-slate-500 rounded-full scale-0 group-hover:scale-100 group-hover:bg-slate-300'
                                  }`}></span>
                                </>
                              )}
                            </NavLink>
                          </li>
                        ))}
                    </ul>
                  </div>
                </li>
              );
            }
            return null;
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-700 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Desktop Sidebar
  if (!isMobile) {
    return isOpen ? (
      <div className="hidden md:block fixed left-0 top-0 h-full w-64 bg-slate-900 text-white shadow-2xl border-r border-slate-700 z-40">
        {sidebarContent}
      </div>
    ) : null;
  }

  // Mobile Sidebar
  return isOpen ? (
    <div className="fixed left-0 top-0 h-full w-72 bg-slate-900 text-white shadow-2xl border-r border-slate-700 z-50 md:hidden">
      {sidebarContent}
    </div>
  ) : null;
};

export default Sidebar;